import React, { useRef, useState, useEffect, useCallback } from 'react';
import * as cls from './carousel-half.module.css';

export const CarouselHalf = ({
  children,
  showArrows = true,
  // gap = 20,
  className = '',
  arrowIcon = '/img/v2/carousel-arrow.svg',
  defaultPerPage = true,
  perPage = 3,
}) => {
  const windowWidth = useRef(null);
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const [animatedItems, setAnimatedItems] = useState(new Set());

  const gap = perPage === 2 ? 40 : 20;

  useEffect(() => {
    windowWidth.current = window.innerWidth;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const items = carouselRef.current?.querySelectorAll(
            `.${cls.carouselItem}`,
          );
          if (items) {
            items.forEach((item, index) => {
              setTimeout(() => {
                setAnimatedItems((prev) => new Set(prev).add(index));
              }, index * 150);
            });
          }
        }
      },
      {
        threshold: 0.3,
        rootMargin: '-50px 0px',
      },
    );

    const currentCarousel = carouselRef.current;
    if (currentCarousel) {
      observer.observe(currentCarousel);
    }

    return () => {
      if (currentCarousel) {
        observer.unobserve(currentCarousel);
      }
    };
  }, []);

  const updateScrollButtons = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > gap);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, [gap]);

  const scrollToNext = () => {
    if (carouselRef.current && canScrollRight) {
      const itemWidth =
        carouselRef.current.children[0].children[0].offsetWidth + gap;
      carouselRef.current.scrollBy({ left: itemWidth, behavior: 'smooth' });
    }
  };

  const scrollToPrev = () => {
    if (carouselRef.current && canScrollLeft) {
      const itemWidth =
        carouselRef.current.children[0].children[0].offsetWidth + gap;
      carouselRef.current.scrollBy({
        left: -itemWidth,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons();

      return () => carousel.removeEventListener('scroll', updateScrollButtons);
    }
  }, [updateScrollButtons]);

  // Convert children to array and add carousel item wrapper
  const carouselItems = React.Children.map(children, (child, index) => (
    <div
      className={`${cls.carouselItem} ${defaultPerPage ? cls.defaultPerPage : ''} ${animatedItems.has(index) ? cls.animated : cls.hidden}`}
      key={index}
      style={{
        flexBasis: defaultPerPage ? undefined : `${100 / perPage}%`,
        flexShrink: defaultPerPage
          ? undefined
          : perPage !== children.length
            ? 0
            : 1,
      }}
    >
      {child}
    </div>
  ));

  return (
    <>
      <div className={`${cls.carouselContainer} ${className}`}>
        <div className={cls.carousel} ref={carouselRef}>
          <div
            className={`${cls.carouselTrack} `}
            style={{
              '--carousel-gap': `${gap}px`,
              padding:
                perPage === 1 || perPage === children.length ? 0 : undefined,
            }}
          >
            {carouselItems}
          </div>
        </div>
      </div>

      {showArrows && perPage !== children.length && (
        <div className={cls.carouselArrows}>
          <button
            className={`${cls.carouselArrow} ${cls.carouselArrowLeft}`}
            onClick={scrollToPrev}
            disabled={!canScrollLeft}
            aria-label="Previous items"
          >
            <img src={arrowIcon} alt="" />
          </button>
          <button
            className={`${cls.carouselArrow} ${cls.carouselArrowRight}`}
            onClick={scrollToNext}
            disabled={!canScrollRight}
            aria-label="Next items"
          >
            <img src={arrowIcon} alt="" />
          </button>
        </div>
      )}
    </>
  );
};
